import React from 'react';
import type {
  UseComboboxState,
  UseComboboxStateChangeOptions,
} from 'downshift';
import { useCombobox } from 'downshift';
import { Text } from '@balance-web/text';

import type { ComboboxItemsStatus, FocusOnMountBehaviour } from './types';

// Message text
// ------------------------------

export const defaultGetMessageText = (status: ComboboxItemsStatus): string => {
  switch (status) {
    case 'error':
      return 'Something went wrong';

    case 'noMatches':
    default:
      return 'No matching results';
  }
};

// Transformers
// ------------------------------

export const defaultItemToDisabled = <T,>(item: T) => {
  if (item === undefined || item === null) {
    throw Error('Invalid combobox item');
  }

  if (typeof item === 'object' && 'disabled' in item) {
    return Boolean(item.disabled);
  }

  return false;
};

export const defaultItemToLabel = <T,>(item: T) => {
  if (item === undefined || item === null) {
    throw Error('Invalid combobox item');
  }

  if (
    typeof item === 'object' &&
    'label' in item &&
    typeof item.label === 'string'
  ) {
    return item.label;
  }

  throw Error('Must provide label when using defaultItemToLabel');
};

export const defaultItemToValue = <T,>(item: T) => {
  if (item === undefined || item === null) {
    throw Error('Invalid combobox item');
  }

  if (
    typeof item === 'object' &&
    'value' in item &&
    (typeof item.value === 'string' || typeof item.value === 'number')
  ) {
    return item.value;
  }

  throw Error('Must provide value when using defaultItemToValue');
};

// Item renderer
// ------------------------------

export function makeDefaultItemRenderer<Item>(
  itemToLabel: (item: Item) => string,
  itemToDisabled: (item: Item) => boolean
) {
  return (item: Item) => {
    const color = itemToDisabled(item) ? 'dim' : undefined;
    return (
      <Text color={color} overflowStrategy="truncate">
        {itemToLabel(item)}
      </Text>
    );
  };
}

// State reducer
// ------------------------------

export function makeDefaultStateReducer<Item>(
  itemToLabel: (item: Item) => string,
  itemToValue: (item: Item) => string | number,
  items: Item[],
  focusOnMount: {
    behaviour?: FocusOnMountBehaviour;
    consumed: boolean;
  },
  consumeFocusOnMount: () => void,
  itemToSelectable?: (item: Item) => boolean
) {
  const stateReducer = (
    state: UseComboboxState<Item>,
    actionAndChanges: UseComboboxStateChangeOptions<Item>
  ) => {
    const { type, changes } = actionAndChanges;

    switch (type) {
      // Reset the input value in cases where the user left it invalid
      case useCombobox.stateChangeTypes.InputClick: {
        const selectedItemFromItems = state.selectedItem
          ? items.find((item) => {
              if (!state.selectedItem) {
                throw Error('state.selectedItem cannot be null at this point');
              }
              return itemToValue(item) === itemToValue(state.selectedItem);
            })
          : undefined;

        const highlightedIndex = getDefaultHighlightedItemIndex({
          items,
          itemToSelectable,
          selectedItem: selectedItemFromItems,
          inputValue: changes.inputValue,
          highlightedIndex: changes.highlightedIndex,
        });

        return {
          ...changes,
          highlightedIndex,
          isOpen: true,
        };
      }

      case useCombobox.stateChangeTypes.InputBlur: {
        if (!changes.inputValue?.length) {
          return {
            ...changes,
            selectedItem: null,
          };
        }

        const inputValue = changes.selectedItem
          ? itemToLabel(changes.selectedItem)
          : changes.inputValue;

        return {
          ...changes,
          inputValue,
        };
      }

      // Maintain the highlighted item when the user mouses out of the menu
      case useCombobox.stateChangeTypes.MenuMouseLeave: {
        return { ...changes, highlightedIndex: state.highlightedIndex };
      }

      case useCombobox.stateChangeTypes.ToggleButtonClick:
      case useCombobox.stateChangeTypes.FunctionOpenMenu: {
        if (focusOnMount.behaviour === 'focus' && !focusOnMount.consumed) {
          consumeFocusOnMount();
          return {
            ...changes,
            isOpen: false,
          };
        } else if (
          focusOnMount.behaviour === 'focus-and-open' &&
          !focusOnMount.consumed
        ) {
          consumeFocusOnMount();
          return changes;
        }

        const selectedItemFromItems = state.selectedItem
          ? items.find((item) => {
              if (!state.selectedItem) {
                throw Error('state.selectedItem cannot be null at this point');
              }
              return itemToValue(item) === itemToValue(state.selectedItem);
            })
          : undefined;

        const highlightedIndex = getDefaultHighlightedItemIndex({
          items,
          itemToSelectable,
          selectedItem: selectedItemFromItems,
          inputValue: changes.inputValue,
          highlightedIndex: changes.highlightedIndex,
        });

        return {
          ...changes,
          highlightedIndex,
        };
      }

      case useCombobox.stateChangeTypes.InputKeyDownEnter: {
        /**
         * When user clears the value and presses enter, we should
         *  clear the selected value.
         */

        /**
         * changes.inputValue !== undefined check is what differentiates between user clearing
         *  input value and hitting enter and there being no change to represent to the input.
         *
         * changes.inputValue can be undefined in a variety of cases and if we fire _internalClear
         *  in these cases it will break keyboard selection.
         */
        const inputClearedByUser =
          changes.inputValue !== undefined && !changes.inputValue.length;

        if (inputClearedByUser && !!state.selectedItem) {
          return {
            ...changes,
            highlightedIndex: -1,
            /**
             * This isn't a valid item but it never makes it to the consumer
             *  onChange, it's intercepted by handleSelectedItemChange.
             */
            selectedItem: {
              id: '_internalClear',
              name: 'Internal clear',
            } as any,
            inputValue: '',
            isOpen: false,
          };
        }

        return {
          ...changes,
          isOpen: false,
        };
      }

      case useCombobox.stateChangeTypes.ItemClick: {
        return {
          ...changes,
          isOpen: false,
        };
      }

      case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem: {
        return {
          ...changes,
          highlightedIndex: !changes.inputValue?.length
            ? -1
            : changes.highlightedIndex,
        };
      }

      // Business as usual
      default:
        if (
          type === useCombobox.stateChangeTypes.InputKeyDownArrowUp ||
          type === useCombobox.stateChangeTypes.InputKeyDownArrowDown ||
          type === useCombobox.stateChangeTypes.ItemMouseMove
        ) {
          return changes;
        }

        const highlightedIndex = getDefaultHighlightedItemIndex({
          items,
          itemToSelectable,
          inputValue: changes.inputValue,
          highlightedIndex: changes.highlightedIndex,
        });

        return { ...changes, highlightedIndex };
    }
  };

  return stateReducer;
}

const getDefaultHighlightedItemIndex = <T,>(args: {
  items: Array<T>;
  itemToSelectable?: (item: T) => boolean;
  selectedItem?: T;
  inputValue?: string;
  highlightedIndex: number | undefined;
}) => {
  if (args.items.length === 1) {
    return 0;
  }

  const firstSelectableItemIndex = (() => {
    const firstItem = args.items[0];
    if (firstItem === null || firstItem === undefined) {
      return -1;
    }

    return args.itemToSelectable
      ? // If first item is not selectable (usually means it's a group label), we use the next item
        args.itemToSelectable(firstItem)
        ? 0
        : 1
      : 0;
  })();

  return args.selectedItem
    ? args.items.indexOf(args.selectedItem) || firstSelectableItemIndex
    : !args.inputValue?.length
    ? undefined
    : firstSelectableItemIndex;
};
